import { fabric } from 'fabric';
import { Canvas } from 'fabric/fabric-impl';
import { useCallback, useEffect, useRef, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { initAligningGuidelines } from '../../utils/canvas-helper';
import { decrypt } from '../../utils/cryptojs-helper';
import { getNextInsertPosition } from './utils/insertPosition';
import Header from './components/Header';
import ToolsSidebar from './components/ToolsSidebar';
import s from './styles.module.scss';
// import { CanvasObject, CanvasTemplate, CanvasTemplateData} from './types';
import { Box, Grid, Typography } from '@mui/material';
import { CanvasObject } from '../../api/digital-signage-cms/types';
import { UserAuthData } from '../../api/user/types';
import Layers from './components/Layers/Layers';
import FontFaceObserver from 'fontfaceobserver';

type Props = {
  user: UserAuthData;
  presentationData: { width: number; height: number };
};

const DEFAULT_BRAND_COLOR_PALETTES = [
  {
    title: 'Brand Colors',
    colors: ['#FF6961', '#2A2C31', '#171719', '#8CB9FF', '#9BE7A1', '#FFAD42', '#FFFFFF', '#000000'],
  },
];

const ContentCreation = ({ user, presentationData }: Props) => {
  const { width, height } = presentationData;
  // const [templates] = useLocalStorageState<string>('templates');
  const [tutorialStep] = useLocalStorageState<number>('tutorial-step');
  const [tutorialTemplates] = useLocalStorageState<string>('tutorialTemplate');
  const [colorPalettes, setColorPalettes] = useLocalStorageState('colorPalettes', {
    defaultValue: DEFAULT_BRAND_COLOR_PALETTES,
  });

  const screenHeight = window.innerHeight * 0.8; // * .85
  const screenWidth = window.innerWidth * 0.8; // * .85

  const dimensions = `${width}x${height}`;
  const canvasWidth = width;
  const canvasHeight = height;
  const leftSidebarWidth = 84;
  const rightSidebarWidth = 260;

  const canvasWrpRef = useRef<HTMLDivElement>(null);
  const canvasDomRef = useRef<HTMLCanvasElement | null>(null);
  const canvas = useRef<Canvas>();
  const undoStackRef = useRef<string[]>([]);
  const redoStackRef = useRef<string[]>([]);
  const processingHistoryRef = useRef<boolean>(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateHistoryAvailability = () => {
    setCanUndo(undoStackRef.current.length > 1);
    setCanRedo(redoStackRef.current.length > 0);
  };

  const findBackgroundLayer = (targetCanvas: Canvas) => {
    const objects = (targetCanvas.getObjects?.() || []) as any[];
    return (
      objects.find((obj) => obj?.isBackgroundLayer === true) ||
      objects.find((obj) => obj?.type === 'rect' && obj?.selectable === false && obj?.evented === false) ||
      objects.find((obj) => obj?.type === 'rect' && obj?.selectable === false) ||
      objects[0]
    );
  };

  const ensureBackgroundLayerOrder = (targetCanvas: Canvas | undefined) => {
    if (!targetCanvas) return;
    const backgroundLayer: any = findBackgroundLayer(targetCanvas);
    if (!backgroundLayer) return;

    backgroundLayer.set({
      isBackgroundLayer: true,
      selectable: false,
      evented: false,
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
    });

    targetCanvas.sendToBack(backgroundLayer);
  };

  const serializeCanvas = (targetCanvas: Canvas | undefined) => {
    if (!targetCanvas) return '';
    return JSON.stringify(targetCanvas.toDatalessJSON(['name', 'isBackgroundLayer']));
  };

  const resetHistoryBaseline = (targetCanvas: Canvas | undefined) => {
    const initialState = serializeCanvas(targetCanvas);
    if (!initialState) return;
    undoStackRef.current = [initialState];
    redoStackRef.current = [];
    updateHistoryAvailability();
  };

  const commitHistorySnapshot = (targetCanvas: Canvas | undefined) => {
    if (!targetCanvas || processingHistoryRef.current) return;

    const nextState = serializeCanvas(targetCanvas);
    if (!nextState) return;

    const currentStack = undoStackRef.current;
    const latestState = currentStack[currentStack.length - 1];
    if (latestState === nextState) return;

    currentStack.push(nextState);
    if (currentStack.length > 100) currentStack.shift();
    redoStackRef.current = [];
    updateHistoryAvailability();
  };

  const restoreCanvasSnapshot = (serializedState: string) => {
    if (!canvas.current || !serializedState) return;

    processingHistoryRef.current = true;
    canvas.current.loadFromJSON(JSON.parse(serializedState), () => {
      ensureBackgroundLayerOrder(canvas.current);
      canvas.current?.renderAll();
      canvas.current?.discardActiveObject();
      processingHistoryRef.current = false;
    });
  };

  const handleUndo = () => {
    if (!canvas.current || undoStackRef.current.length <= 1 || processingHistoryRef.current) return;

    const currentState = undoStackRef.current.pop();
    if (currentState) redoStackRef.current.push(currentState);

    const previousState = undoStackRef.current[undoStackRef.current.length - 1];
    if (previousState) restoreCanvasSnapshot(previousState);
    updateHistoryAvailability();
  };

  const handleRedo = () => {
    if (!canvas.current || redoStackRef.current.length === 0 || processingHistoryRef.current) return;

    const nextState = redoStackRef.current.pop();
    if (!nextState) return;

    undoStackRef.current.push(nextState);
    restoreCanvasSnapshot(nextState);
    updateHistoryAvailability();
  };

  const resetFocus = () => {
    canvas.current?.discardActiveObject().renderAll();
    canvas.current?.fire('focus:reset');
  };

  const handleDownload = (fileName: string) => {
    const zoom = canvas.current?.getZoom() || 0.68;
    const multi = ((canvasHeight * canvasWidth) / (canvasHeight * zoom * (canvasWidth * zoom))) * zoom;

    const url =
      canvas.current?.toDataURL({
        format: 'png',
        multiplier: multi,
      }) || '';
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  };

  useEffect(() => {
    if (!canvasDomRef.current || canvas.current) return;

    const screenArea = screenHeight * screenWidth;
    const canvasArea = canvasHeight * canvasWidth;
    let scale = 0;
    if (canvasArea > screenArea) scale = screenArea / canvasArea;
    else if (canvasArea < screenArea) scale = canvasArea / screenArea;
    if (scale > 0.7) scale = 0.68;

    canvas.current = new fabric.Canvas(canvasDomRef.current, { backgroundColor: '#eeeeee' });
    initAligningGuidelines(canvas.current);
    canvas.current.setZoom(scale);
    canvas.current.setHeight(canvasHeight * canvas.current.getZoom());
    canvas.current.setWidth(canvasWidth * canvas.current.getZoom());

    const rect = new fabric.Rect({
      height: canvasHeight,
      width: canvasWidth,
      fill: '#eeeeee',
      selectable: false,
      evented: false,
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
    });
    (rect as any).isBackgroundLayer = true;
    canvas.current.add(rect);
    ensureBackgroundLayerOrder(canvas.current);
    canvas.current.renderAll();
  }, [canvasHeight, canvasWidth, screenHeight, screenWidth]);

  useEffect(() => {
    return () => {
      canvas.current?.dispose();
      canvas.current = undefined;
    };
  }, []);

  // functionality for deleting
  const handleEventKeyPress = (event: KeyboardEvent) => {
    const activeObject: any = canvas.current?.getActiveObject();
    // check to see if there is another active input focused. for now the font-size input
    // is what we need to ignore
    const activeElement = document.activeElement as HTMLElement | null;
    const isTypingInput =
      (activeElement && activeElement.id === 'font-size-input') ||
      activeElement?.tagName === 'INPUT' ||
      activeElement?.tagName === 'TEXTAREA' ||
      activeElement?.isContentEditable;
    if (isTypingInput) return;

    if (!canvas.current) return;

    const isMeta = event.ctrlKey || event.metaKey;

    if (isMeta && !event.shiftKey && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      handleUndo();
      return;
    }

    if ((isMeta && event.shiftKey && event.key.toLowerCase() === 'z') || (isMeta && event.key.toLowerCase() === 'y')) {
      event.preventDefault();
      handleRedo();
      return;
    }

    if (event.key === 'Escape') {
      canvas.current.discardActiveObject().renderAll();
      return;
    }

    if (isMeta && event.key.toLowerCase() === 'd' && activeObject) {
      event.preventDefault();
      activeObject.clone((cloned: any) => {
        const clonedObj = cloned;
        if (!clonedObj) return;
        const nextPosition = getNextInsertPosition(canvas.current, { cascadeCount: 32 });
        clonedObj.set({ left: nextPosition.left, top: nextPosition.top });
        canvas.current?.add(clonedObj);
        canvas.current?.setActiveObject(clonedObj);
        canvas.current?.requestRenderAll();
      });
      return;
    }

    if (isMeta && (event.key === ']' || event.key === '[') && activeObject) {
      event.preventDefault();
      const objects = canvas.current.getObjects();
      const currentIndex = objects.indexOf(activeObject);

      if (currentIndex <= 0) return;

      if (event.key === ']') {
        if (currentIndex < objects.length - 1) {
          canvas.current.moveTo(activeObject, currentIndex + 1).renderAll();
        }
      } else {
        // Keep layer index 0 reserved for background to avoid visual disappearance.
        if (currentIndex > 1) {
          canvas.current.moveTo(activeObject, currentIndex - 1).renderAll();
        }
      }
      canvas.current.fire('object:modified');
      return;
    }

    if (isMeta && event.key.toLowerCase() === 'l' && activeObject) {
      event.preventDefault();
      const isLocked = Boolean(activeObject.lockMovementX);
      activeObject.set({
        selectable: isLocked,
        lockMovementX: !isLocked,
        lockMovementY: !isLocked,
        lockRotation: !isLocked,
        lockScalingFlip: !isLocked,
        lockScalingY: !isLocked,
        lockScalingX: !isLocked,
        lockSkewingY: !isLocked,
        lockSkewingX: !isLocked,
      });
      canvas.current.requestRenderAll();
      return;
    }

    if (activeObject && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
      const distance = event.shiftKey ? 10 : 1;
      if (event.key === 'ArrowUp') activeObject.top = (activeObject.top || 0) - distance;
      if (event.key === 'ArrowDown') activeObject.top = (activeObject.top || 0) + distance;
      if (event.key === 'ArrowLeft') activeObject.left = (activeObject.left || 0) - distance;
      if (event.key === 'ArrowRight') activeObject.left = (activeObject.left || 0) + distance;
      activeObject.setCoords();
      canvas.current.requestRenderAll();
      return;
    }

    if (event.key === 'Backspace' || event.code === '8' || event.key === 'Delete' || event.code === '46') {
      // delete all Objects but textbox type
      if (activeObject && !activeObject.isType('textbox')) {
        canvas?.current?.remove(activeObject);
        canvas?.current?.renderAll();
      } // delete textbox if it is currently not being edited
      else if (activeObject && activeObject.isType('textbox') && !activeObject.isEditing) {
        canvas?.current?.remove(activeObject);
        canvas?.current?.renderAll();
      }
    }
  };
  // GRABBED FROM TemplateModule
  const transformObjects = (objects: CanvasObject[], scale: number) => {
    const transformedObjects: CanvasObject[] = [];
    objects.map((obj: CanvasObject) => {
      if (obj && obj.scaleX && obj.scaleY && scale > 1) {
        obj.scaleX = obj.scaleX * (scale * 0.75);
        obj.scaleY = obj.scaleY * (scale * 0.75);
      } else if (obj && obj.scaleX && obj.scaleY) {
        obj.scaleX = obj.scaleX * (scale * 1.5);
        obj.scaleY = obj.scaleY * (scale * 1.5);
      }
      transformedObjects.push(obj);
    });
    return transformedObjects;
  };

  // adding an event listeren to key down pressed
  useEffect(() => {
    // Check to see if we are currently on the template step
    if (tutorialStep === 5 && tutorialTemplates) {
      const decryptedTemplates: string = decrypt(tutorialTemplates);
      const templateObject = JSON.parse(decryptedTemplates);
      const JSONData = templateObject[0].data;
      if (JSONData) {
        const ogHeight = JSONData.height ? JSONData.height : 1080;
        const ogWidth = JSONData.width ? JSONData.width : 1920;

        const ogArea = ogHeight * ogWidth;
        const newArea = height * width;

        const scale = newArea / ogArea;

        const loadJSON = {
          background: JSONData.background,
          objects: scale == 1 ? JSONData.objects : transformObjects(JSONData.objects, scale),
          version: JSONData.version,
        };
        canvas.current?.clear();
        canvas.current?.loadFromJSON(loadJSON, function () {
          canvas.current?.renderAll();
            resetHistoryBaseline(canvas.current);
        });
      }
    }
    const fonts = [
      { name: 'Open Sans', class: 'open-sans' },
      { name: 'EB Garamond', class: 'eb-garamond' },
      { name: 'Jost', class: 'jost' },
      { name: 'Inter', class: 'inter' },
      { name: 'Bodoni Moda', class: 'bodoni-moda' },
      { name: 'Martian Mono', class: 'martian-mono' },
      { name: 'Gothic A1', class: 'gothic-a1' },
      { name: 'Source Serif 4', class: 'source-serif-4' },
      { name: 'Arvo', class: 'arvo' },
      { name: 'Libre Franklin', class: 'libre-franklin' },
      { name: 'Roboto', class: 'roboto' },
      { name: 'La Belle Aurore', class: 'la-belle-aurore' },
    ];
    for (let i = 0; i < fonts.length; i++) {
      const myfont = new FontFaceObserver(fonts[i].name);
      myfont.load();
    }
    // keep below
    document.addEventListener('keydown', handleEventKeyPress);
    return function cleanup() {
      document.removeEventListener('keydown', handleEventKeyPress);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let unbind: (() => void) | undefined;
    let waitForCanvasTimer: ReturnType<typeof setInterval> | undefined;

    const bindHistoryListeners = (targetCanvas: Canvas) => {
      const onHistoryChange = () => commitHistorySnapshot(targetCanvas);
      const onObjectAdded = () => {
        ensureBackgroundLayerOrder(targetCanvas);
        onHistoryChange();
      };

      targetCanvas.on('object:added', onObjectAdded);
      targetCanvas.on('object:removed', onHistoryChange);
      targetCanvas.on('object:modified', onHistoryChange);
      targetCanvas.on('path:created', onHistoryChange);

      ensureBackgroundLayerOrder(targetCanvas);
      resetHistoryBaseline(targetCanvas);

      return () => {
        targetCanvas.off('object:added', onObjectAdded);
        targetCanvas.off('object:removed', onHistoryChange);
        targetCanvas.off('object:modified', onHistoryChange);
        targetCanvas.off('path:created', onHistoryChange);
      };
    };

    if (canvas.current) {
      unbind = bindHistoryListeners(canvas.current);
    } else {
      waitForCanvasTimer = setInterval(() => {
        if (!canvas.current || unbind) return;
        unbind = bindHistoryListeners(canvas.current);
        if (waitForCanvasTimer) {
          clearInterval(waitForCanvasTimer);
          waitForCanvasTimer = undefined;
        }
      }, 100);
    }

    return () => {
      if (waitForCanvasTimer) clearInterval(waitForCanvasTimer);
      if (unbind) unbind();
    };
  }, []);

  useEffect(() => {
    if (!colorPalettes || colorPalettes.length === 0) {
      setColorPalettes(DEFAULT_BRAND_COLOR_PALETTES);
      return;
    }

    const hasOnlyLegacyBasicPalette =
      colorPalettes.length === 1 && colorPalettes[0]?.title?.toLowerCase() === 'basic colors';

    if (hasOnlyLegacyBasicPalette) {
      setColorPalettes(DEFAULT_BRAND_COLOR_PALETTES);
    }
  }, [colorPalettes, setColorPalettes]);

  return (
    <>
      <Grid container position={'relative'} height={'88vh'} width={'100%'} wrap={'nowrap'}>
        <Grid
          item
          height={'100%'}
          sx={{
            width: `${leftSidebarWidth}px`,
            minWidth: `${leftSidebarWidth}px`,
            maxWidth: `${leftSidebarWidth}px`,
          }}
        >
          <ToolsSidebar canvas={canvas} canvasHeight={canvasHeight} canvasWidth={canvasWidth} colorPalettes={colorPalettes} setColorPalettes={setColorPalettes} />
        </Grid>
        <Grid item height={'100%'} sx={{ flex: 1, minWidth: 0 }}>
          <Grid container height={'100%'} display={'grid'} gridTemplateRows={'10% 90%'}>
            <Grid item xs={12}>
              <Header
                canvas={canvas}
                handleDownload={handleDownload}
                canvasHeight={canvasHeight}
                canvasWidth={canvasWidth}
                user={user}
                onUndo={handleUndo}
                onRedo={handleRedo}
                canUndo={canUndo}
                canRedo={canRedo}
              />
            </Grid>
            <Grid item xs={12} height={'100%'} bgcolor={'#171719'}>
              <Box
                ref={canvasWrpRef}
                height={'100%'}
                position={'relative'}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <div className={s.canvasWrpBackground} onClick={() => resetFocus()}></div>
                <canvas ref={canvasDomRef} className={s.canvas} />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          height={'100%'}
          sx={{
            width: `${rightSidebarWidth}px`,
            minWidth: `${rightSidebarWidth}px`,
            maxWidth: `${rightSidebarWidth}px`,
          }}
        >
          <Layers canvas={canvas} colorPalettes={colorPalettes} setColorPalettes={setColorPalettes} />
        </Grid>
        <Box position={'absolute'} bottom={'1%'} right={`${rightSidebarWidth + 16}px`}>
          <Typography color={'#fff'}>{dimensions}</Typography>
        </Box>
      </Grid>
    </>
    // </div>
  );
};

export default ContentCreation;
